import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isSameDay, isToday, addDays } from "date-fns";
import { Search, X, UserPlus, Edit, Calendar as CalendarIcon, ChevronLeft, ChevronRight, FileText, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import { API, formatApiError, getInitials, fmtTime12, fmtHour12 } from "@/lib/shared";

export const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 8; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      out.push(`${hh}:${mm}`);
    }
  }
  out.push('00:00'); // 12:00 AM (midnight, end of day)
  return out;
})();

// Default booking times: every 2 hours from 8 AM → 12 AM (midnight)
export const TIME_OPTIONS_PRESET = (() => {
  const out = [];
  for (let h = 8; h < 24; h += 2) {
    out.push(`${String(h).padStart(2, '0')}:00`);
  }
  out.push('00:00'); // midnight
  return out;
})();

export const HOUR_SLOTS = Array.from({ length: 17 }, (_, i) => i + 7); // 7..23

export const BigCalendar = ({ appointments, patients, staff, serviceTypes, onRefresh, onEditAppointment, onCheckIn, onViewPatient, focusDate, onDateChange }) => {
  const [viewMonth, setViewMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  useEffect(() => { if (selectedDate) onDateChange?.(selectedDate); }, [selectedDate, onDateChange]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockStart, setBlockStart] = useState('14:00');
  const [blockDuration, setBlockDuration] = useState(60);
  const [blockReason, setBlockReason] = useState('');
  const [blockSaving, setBlockSaving] = useState(false);
  // Recurring-block state
  const [blockRepeat, setBlockRepeat] = useState(false);
  const [blockWeeks, setBlockWeeks] = useState(4);
  const [blockDays, setBlockDays] = useState([]); // JS getDay convention: 0=Sun..6=Sat (converted to Mon=0..Sun=6 when calling the API)

  // Booking form state
  const [patientId, setPatientId] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [patientPopoverOpen, setPatientPopoverOpen] = useState(false);
  const [serviceId, setServiceId] = useState('');
  const [staffIds, setStaffIds] = useState([]);
  const [time, setTime] = useState('08:00');
  const [showAllTimes, setShowAllTimes] = useState(false);
  const [notes, setNotes] = useState('');
  const [serviceAddress, setServiceAddress] = useState('');
  const [parkingInstructions, setParkingInstructions] = useState('');
  const [saving, setSaving] = useState(false);
  const [repeatFreq, setRepeatFreq] = useState('none');
  const [repeatCount, setRepeatCount] = useState(4);
  const [seriesPreview, setSeriesPreview] = useState(null);
  const [skipConflicts, setSkipConflicts] = useState(true);

  // Quick new-patient state
  const [newPatientOpen, setNewPatientOpen] = useState(false);
  const [newPatient, setNewPatient] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [creatingPatient, setCreatingPatient] = useState(false);

  const resetForm = useCallback(() => {
    setPatientId(''); setPatientSearch(''); setServiceId(''); setStaffIds([]); setTime('08:00'); setNotes(''); setShowAllTimes(false); setServiceAddress(''); setParkingInstructions(''); setRepeatFreq('none'); setRepeatCount(4); setSeriesPreview(null);
  }, []);


  // When parent asks us to focus a specific date (e.g. from the website-bookings banner),
  // jump the month grid AND select that day so the chip is immediately visible.
  useEffect(() => {
    if (!focusDate) return;
    const d = focusDate instanceof Date ? focusDate : new Date(focusDate);
    if (Number.isNaN(d.getTime())) return;
    setViewMonth(startOfMonth(d));
    setSelectedDate(d);
  }, [focusDate]);

  const days = useMemo(() => {
    const start = startOfMonth(viewMonth);
    const end = endOfMonth(viewMonth);
    const offset = getDay(start);
    const padded = [];
    for (let i = 0; i < offset; i++) padded.push(null);
    return [...padded, ...eachDayOfInterval({ start, end })];
  }, [viewMonth]);

  const apptsByDay = useMemo(() => {
    const m = {};
    for (const a of appointments) {
      const k = a.start_datetime?.slice(0, 10);
      if (!k) continue;
      (m[k] = m[k] || []).push(a);
    }
    Object.values(m).forEach(arr => arr.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime)));
    return m;
  }, [appointments]);

  const patientById = useMemo(() => Object.fromEntries(patients.map(p => [p.id, p])), [patients]);
  const serviceById = useMemo(() => Object.fromEntries(serviceTypes.map(s => [s.id, s])), [serviceTypes]);

  const buildSeriesPayload = useCallback(() => {
    if (!selectedDate || !serviceId) return null;
    const svc = serviceById[serviceId];
    const duration = svc?.duration_minutes || 60;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const startMs = new Date(`${dateStr}T${time}:00`).getTime();
    return { patient_id: patientId || 'preview', staff_ids: staffIds, service_type_id: serviceId, start_datetime: new Date(startMs).toISOString(), end_datetime: new Date(startMs + duration * 60000).toISOString(), status: 'scheduled', frequency: repeatFreq, occurrences: repeatCount, tz: Intl.DateTimeFormat().resolvedOptions().timeZone };
  }, [selectedDate, serviceId, serviceById, time, patientId, staffIds, repeatFreq, repeatCount]);

  useEffect(() => {
    if (repeatFreq === 'none') { setSeriesPreview(null); return; }
    const payload = buildSeriesPayload();
    if (!payload) { setSeriesPreview(null); return; }
    const t = setTimeout(() => {
      axios.post(`${API}/appointments/series/preview`, payload).then(r => setSeriesPreview(r.data)).catch(() => setSeriesPreview(null));
    }, 350);
    return () => clearTimeout(t);
  }, [repeatFreq, buildSeriesPayload]);
  const staffById = useMemo(() => Object.fromEntries(staff.map(s => [s.id, s])), [staff]);

  const filteredPatients = useMemo(() => {
    const q = patientSearch.trim().toLowerCase();
    if (!q) return patients.slice(0, 50);
    return patients.filter(p => `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) || (p.phone || '').includes(q));
  }, [patients, patientSearch]);

  const grouped = useMemo(() => {
    const groups = { hydration: [], iv_drip: [], injection: [], other: [] };
    for (const s of serviceTypes) {
      (groups[s.category] || groups.other).push(s);
    }
    return groups;
  }, [serviceTypes]);

  const handleSelectDate = (d) => {
    setSelectedDate(d);
    resetForm();
    setShowBlockForm(false);
  };

  // Fetch blocked slots for current month for cell rendering + on date select
  const fetchBlockedSlots = useCallback(async () => {
    try {
      const start = format(startOfMonth(viewMonth), 'yyyy-MM-dd');
      const end = format(endOfMonth(viewMonth), 'yyyy-MM-dd');
      const res = await axios.get(`${API}/blocked-slots`, { params: { start_date: start, end_date: end } });
      setBlockedSlots(res.data);
    } catch { /* silent */ }
  }, [viewMonth]);

  useEffect(() => { fetchBlockedSlots(); }, [fetchBlockedSlots]);

  const blocksByDay = useMemo(() => {
    const m = {};
    for (const b of blockedSlots) {
      (m[b.date] = m[b.date] || []).push(b);
    }
    return m;
  }, [blockedSlots]);

  const selectedDateBlocks = useMemo(() => {
    if (!selectedDate) return [];
    return blocksByDay[format(selectedDate, 'yyyy-MM-dd')] || [];
  }, [selectedDate, blocksByDay]);

  // Day-view: group selected date's appointments into hour buckets (7 AM..11 PM).
  // Appointments starting before 7 AM go into the 7 AM bucket; after 11 PM into the 11 PM bucket.
  const daySlotByHour = useMemo(() => {
    const m = {};
    if (!selectedDate) return m;
    const key = format(selectedDate, 'yyyy-MM-dd');
    const list = (apptsByDay[key] || []);
    for (const a of list) {
      const dt = new Date(a.start_datetime);
      let h = dt.getHours();
      if (h < 7) h = 7;
      if (h > 23) h = 23;
      (m[h] = m[h] || []).push(a);
    }
    return m;
  }, [selectedDate, apptsByDay]);

  const daySlotBlocksByHour = useMemo(() => {
    const m = {};
    for (const b of selectedDateBlocks) {
      const [hh] = (b.start_time || '00:00').split(':').map(Number);
      let h = hh;
      if (h < 7) h = 7;
      if (h > 23) h = 23;
      (m[h] = m[h] || []).push(b);
    }
    return m;
  }, [selectedDateBlocks]);

  const addEndTime = (start, minutes) => {
    const [h, m] = start.split(':').map(Number);
    const totalMin = h * 60 + m + minutes;
    if (totalMin >= 24 * 60) return '24:00';
    const eh = Math.floor(totalMin / 60);
    const em = totalMin % 60;
    return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
  };

  const handleBlockSlot = async () => {
    if (!selectedDate) return;
    setBlockSaving(true);
    try {
      const endTime = addEndTime(blockStart, blockDuration);

      if (blockRepeat) {
        // Recurring block across N weeks + selected weekdays.
        // Fall back to the selectedDate's weekday if user didn't pick any.
        // JS getDay: 0=Sun..6=Sat. Backend expects 0=Mon..6=Sun -> convert.
        let daysUi = blockDays.length ? blockDays : [selectedDate.getDay()];
        const daysApi = daysUi.map(d => (d === 0 ? 6 : d - 1));
        const res = await axios.post(`${API}/blocked-slots/bulk`, {
          start_date: format(selectedDate, 'yyyy-MM-dd'),
          weeks: blockWeeks,
          days_of_week: daysApi,
          start_time: blockStart,
          end_time: endTime,
          reason: blockReason || null,
        });
        const created = res.data?.created_count || 0;
        const skipped = res.data?.skipped_existing || 0;
        toast.success(`Blocked ${created} day${created === 1 ? '' : 's'} · ${fmtTime12(blockStart)}–${fmtTime12(endTime)}${skipped ? ` (${skipped} already blocked)` : ''}`);
      } else {
        await axios.post(`${API}/blocked-slots`, {
          date: format(selectedDate, 'yyyy-MM-dd'),
          start_time: blockStart,
          end_time: endTime,
          reason: blockReason || null,
        });
        toast.success(`Blocked ${fmtTime12(blockStart)}–${fmtTime12(endTime)}`);
      }

      setShowBlockForm(false);
      setBlockReason('');
      setBlockRepeat(false);
      setBlockDays([]);
      setBlockWeeks(4);
      fetchBlockedSlots();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Failed to block'); }
    finally { setBlockSaving(false); }
  };

  const handleUnblock = async (id) => {
    try {
      await axios.delete(`${API}/blocked-slots/${id}`);
      toast.success('Time unblocked');
      fetchBlockedSlots();
    } catch (err) { toast.error('Failed to unblock'); }
  };

  const handleUnblockRecurrence = async (block) => {
    if (!selectedDate) return;
    // JS getDay: 0=Sun..6=Sat  →  API expects Mon=0..Sun=6
    const jsDow = selectedDate.getDay();
    const apiDow = jsDow === 0 ? 6 : jsDow - 1;
    const weekdayLabel = format(selectedDate, 'EEEE');
    const ok = window.confirm(
      `Unblock every future ${weekdayLabel} at ${fmtTime12(block.start_time)}–${fmtTime12(block.end_time)}?\n\nThis removes ${weekdayLabel}s from today onward. Past blocks stay untouched.`
    );
    if (!ok) return;
    try {
      const res = await axios.post(`${API}/blocked-slots/unblock-recurrence`, {
        start_time: block.start_time,
        end_time: block.end_time,
        day_of_week: apiDow,
        from_date: format(selectedDate, 'yyyy-MM-dd'),
      });
      const n = res.data?.deleted_count || 0;
      toast.success(n ? `Unblocked ${n} ${weekdayLabel}${n === 1 ? '' : 's'}` : 'No matching future blocks');
      fetchBlockedSlots();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Failed to unblock series'); }
  };

  const handleCreatePatient = async () => {
    if (!newPatient.first_name || !newPatient.last_name) { toast.error('First and last name required'); return; }
    setCreatingPatient(true);
    try {
      const res = await axios.post(`${API}/patients`, { ...newPatient });
      toast.success('Patient created');
      setPatientId(res.data.id);
      setNewPatientOpen(false);
      setNewPatient({ first_name: '', last_name: '', phone: '', email: '' });
      onRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCreatingPatient(false); }
  };

  const handleSave = async () => {
    if (!selectedDate) { toast.error('Pick a date first'); return; }
    if (!patientId) { toast.error('Select a patient'); return; }
    if (!serviceId) { toast.error('Select a service'); return; }
    const svc = serviceById[serviceId];
    const duration = svc?.duration_minutes || 60;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const startISO = new Date(`${dateStr}T${time}:00`).toISOString();
    const endISO = new Date(new Date(`${dateStr}T${time}:00`).getTime() + duration * 60000).toISOString();
    setSaving(true);
    try {
      const payload = {
        patient_id: patientId,
        staff_ids: staffIds,
        service_type_id: serviceId,
        start_datetime: startISO,
        end_datetime: endISO,
        notes: notes || null,
        status: 'scheduled',
        service_address: serviceAddress || null,
        parking_instructions: parkingInstructions || null,
      };
      if (repeatFreq !== 'none') {
        const conflicting = (seriesPreview?.occurrences || []).filter(o => o.conflicts.length).map(o => o.index);
        if (conflicting.length && !skipConflicts && !window.confirm(`${conflicting.length} visit${conflicting.length === 1 ? '' : 's'} in this series conflict with blocked time or other appointments. Book them anyway?`)) { setSaving(false); return; }
        const params = conflicting.length && skipConflicts ? { skip_indices: conflicting.join(',') } : {};
        const r = await axios.post(`${API}/appointments/series`, { ...payload, frequency: repeatFreq, occurrences: repeatCount }, { params });
        toast.success(`Booked ${r.data.count} repeating visits${r.data.skipped?.length ? ` · skipped ${r.data.skipped.length} conflicting` : ''}`);
      } else {
        await axios.post(`${API}/appointments`, payload);
        toast.success('Appointment booked');
      }
      resetForm();
      onRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Booking failed'); }
    finally { setSaving(false); }
  };

  const weekDayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const statusColor = (s) => ({
    scheduled: 'bg-primary/20 text-primary border-primary/40',
    checked_in: 'bg-amber-500/20 text-amber-300 border-amber-500/50',
    completed: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50',
    cancelled: 'bg-destructive/15 text-destructive border-destructive/40',
    'no-show': 'bg-destructive/15 text-destructive border-destructive/40',
    no_show: 'bg-destructive/15 text-destructive border-destructive/40',
  }[s] || 'bg-muted/40 text-muted-foreground border-border/40');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      {/* LEFT: Big Calendar */}
      <Card className="lg:col-span-8 gold-border bg-card/80" data-testid="big-calendar">
        <CardContent className="p-4">
          {!selectedDate ? (
            // ================= MONTH VIEW =================
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => setViewMonth(subMonths(viewMonth, 1))} data-testid="cal-prev-month"><ChevronLeft className="w-5 h-5" /></Button>
                  <h2 className="text-xl font-semibold brand-heading"><span className="gold-gradient-text">{format(viewMonth, 'MMMM yyyy')}</span></h2>
                  <Button variant="ghost" size="icon" onClick={() => setViewMonth(addMonths(viewMonth, 1))} data-testid="cal-next-month"><ChevronRight className="w-5 h-5" /></Button>
                </div>
                <Button variant="outline" size="sm" onClick={() => { setViewMonth(new Date()); setSelectedDate(new Date()); }} className="border-primary/40 text-primary hover:bg-primary/10" data-testid="cal-today">Today</Button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-1">
                {weekDayLabels.map(d => (
                  <div key={d} className="text-[11px] font-semibold text-primary/80 uppercase tracking-wider text-center py-1">{d}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1.5">
                {days.map((d, idx) => {
                  if (!d) return <div key={`empty-${idx}`} className="aspect-square rounded-md bg-muted/5" />;
                  const key = format(d, 'yyyy-MM-dd');
                  const dayAppts = apptsByDay[key] || [];
                  const dayBlocks = blocksByDay[key] || [];
                  const pending = dayAppts.filter(a => a.confirmed === false).length;
                  const isPast = d < new Date(new Date().setHours(0, 0, 0, 0));
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleSelectDate(d)}
                      data-testid={`cal-day-${key}`}
                      className={`aspect-square rounded-lg p-2 text-left transition-all border flex flex-col ${
                        isToday(d) ? 'border-primary bg-primary/10 shadow-[0_0_0_1px_rgba(212,175,55,0.35)]' :
                        'border-border/30 bg-muted/10 hover:border-primary/60 hover:bg-primary/5 hover:scale-[1.02]'
                      } ${isPast ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-lg font-bold leading-none ${isToday(d) ? 'text-primary' : 'text-foreground'}`}>{format(d, 'd')}</span>
                        {dayAppts.length > 0 && (
                          <span className="text-[9px] font-bold text-primary bg-primary/15 rounded-full min-w-[18px] px-1 py-0.5 text-center leading-none" data-testid={`cal-day-count-${key}`}>{dayAppts.length}</span>
                        )}
                      </div>
                      <div className="flex-1 flex items-end justify-start gap-0.5 flex-wrap mt-1">
                        {dayAppts.slice(0, 5).map((a, i) => (
                          <span
                            key={i}
                            className={`inline-block w-1.5 h-1.5 rounded-full ${a.confirmed === false ? 'bg-orange-400 animate-pulse' : a.status === 'completed' ? 'bg-emerald-400/80' : a.status === 'checked_in' ? 'bg-amber-400/80' : 'bg-primary/70'}`}
                          />
                        ))}
                        {dayAppts.length > 5 && <span className="text-[8px] text-primary/60 font-semibold">+{dayAppts.length - 5}</span>}
                        {dayBlocks.length > 0 && <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-400/80" title="Blocked time" />}
                      </div>
                      {pending > 0 && (
                        <p className="text-[8px] font-bold uppercase tracking-wider text-orange-300 mt-0.5">{pending} pending</p>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground italic mt-3 text-center">Click any day to open its hourly schedule</p>
            </>
          ) : (
            // ================= DAY VIEW =================
            <>
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedDate(null)} data-testid="cal-back-to-month" className="text-primary hover:bg-primary/10 gap-1 h-8 px-2">
                    <ChevronLeft className="w-4 h-4" /> Month
                  </Button>
                  <h2 className="text-xl font-semibold brand-heading truncate"><span className="gold-gradient-text">{format(selectedDate, 'EEEE, MMM d')}</span></h2>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => setSelectedDate(addDays(selectedDate, -1))} data-testid="cal-prev-day" title="Previous day"><ChevronLeft className="w-5 h-5" /></Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedDate(new Date())} data-testid="cal-day-today" className="border-primary/40 text-primary hover:bg-primary/10 h-8 px-2 text-xs">Today</Button>
                  <Button variant="ghost" size="icon" onClick={() => setSelectedDate(addDays(selectedDate, 1))} data-testid="cal-next-day" title="Next day"><ChevronRight className="w-5 h-5" /></Button>
                </div>
              </div>

              <div className="rounded-lg border border-primary/25 overflow-hidden divide-y divide-border/25 max-h-[640px] overflow-y-auto" data-testid="cal-day-timeline">
                {HOUR_SLOTS.map(hour => {
                  const label = fmtHour12(hour);
                  const slotAppts = daySlotByHour[hour] || [];
                  const slotBlocks = daySlotBlocksByHour[hour] || [];
                  const isEmpty = slotAppts.length === 0 && slotBlocks.length === 0;
                  const hh = String(hour).padStart(2, '0');
                  const isCurrentHour = isSameDay(selectedDate, new Date()) && new Date().getHours() === hour;
                  return (
                    <div
                      key={hour}
                      className={`grid grid-cols-[68px_1fr] min-h-[54px] transition-colors group ${isEmpty ? 'hover:bg-primary/5 cursor-pointer' : ''} ${isCurrentHour ? 'bg-primary/[0.04]' : ''}`}
                      data-testid={`day-slot-${hh}`}
                      onClick={isEmpty ? () => { setTime(`${hh}:00`); } : undefined}
                      title={isEmpty ? `Click to book at ${label}` : undefined}
                    >
                      <div className={`border-r border-border/40 bg-muted/5 flex items-start justify-end pt-2 pr-3 ${isCurrentHour ? 'bg-primary/10' : ''}`}>
                        <span className={`text-[11px] font-semibold uppercase tracking-wider ${isCurrentHour ? 'text-primary' : 'text-primary/70'}`}>{label}</span>
                      </div>
                      <div className="p-1.5 space-y-1 min-w-0">
                        {slotBlocks.map(b => (
                          <div key={b.id} className="text-[11px] rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-300 px-2 py-1 truncate" data-testid={`day-block-${b.id}`}>
                            🚫 {fmtTime12(b.start_time)}–{fmtTime12(b.end_time)}{b.reason ? ` · ${b.reason}` : ''}
                          </div>
                        ))}
                        {slotAppts.map(apt => {
                          const p = patientById[apt.patient_id];
                          const svc = serviceById[apt.service_type_id];
                          const dt = new Date(apt.start_datetime);
                          const timeLabel = format(dt, 'p');
                          const apptStaff = (apt.staff_ids || []).map(id => staffById[id]).filter(Boolean);
                          const patientName = p ? `${p.first_name} ${p.last_name}` : 'Unknown patient';
                          return (
                            <HoverCard key={apt.id} openDelay={80} closeDelay={120}>
                              <HoverCardTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); onEditAppointment?.(apt); }}
                                  data-testid={`day-slot-appt-${apt.id}`}
                                  className={`w-full text-left flex items-center gap-2 rounded-md border px-2.5 py-1.5 transition-all hover:shadow-md hover:shadow-primary/10 ${statusColor(apt.status)} ${apt.confirmed === false ? 'ring-1 ring-orange-500/60 animate-pulse' : ''}`}
                                >
                                  <span className="text-[10px] font-mono uppercase tracking-wider text-primary/80 shrink-0 w-16">{timeLabel}</span>
                                  <span className="text-sm font-semibold truncate flex-1">
                                    {apt.confirmed === false && <span className="text-orange-300 mr-1">⚠</span>}
                                    {patientName}
                                  </span>
                                  <span className="text-[10px] text-muted-foreground truncate max-w-[45%] hidden sm:inline">{svc?.name || '—'}</span>
                                </button>
                              </HoverCardTrigger>
                              <HoverCardContent side="right" align="start" className="w-80 bg-popover border-primary/30 gold-glow" data-testid={`day-slot-tooltip-${apt.id}`}>
                                <div className="space-y-2">
                                  <div>
                                    <p className="text-sm font-semibold text-primary">{patientName}</p>
                                    <p className="text-[11px] text-muted-foreground">{format(dt, 'EEE MMM d')} · {timeLabel}</p>
                                    <p className="text-[11px] text-foreground">{svc?.name}{(apt.add_on_service_ids || []).length > 0 && <span className="text-primary"> + {apt.add_on_service_ids.map(id => serviceById[id]?.name).filter(Boolean).join(' + ')}</span>}</p>
                                  </div>
                                  {apt.confirmed === false && (
                                    <Badge variant="outline" className="text-[10px] border-orange-500/50 text-orange-300 bg-orange-500/15">⚠ PENDING · Booked via website</Badge>
                                  )}
                                  {p && (
                                    p.intake_completed_at
                                      ? <Badge variant="outline" className="text-[10px] border-emerald-500/50 text-emerald-300 bg-emerald-500/10">✓ Intake completed</Badge>
                                      : <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-300/80 bg-amber-500/5">⏳ Intake pending</Badge>
                                  )}
                                  <div className="text-xs space-y-1">
                                    <p><span className="text-muted-foreground">Ordered: </span><span className="text-foreground font-medium">{svc?.name || '—'}</span>{svc?.price ? <span className="text-primary/80"> · ${Number(svc.price).toFixed(0)}</span> : null}</p>
                                    {apptStaff.length > 0 && <p><span className="text-muted-foreground">Nurse: </span>{apptStaff.map(s => `${s.first_name} ${s.last_name}`).join(', ')}</p>}
                                    {p?.phone && <p><span className="text-muted-foreground">Phone: </span>{p.phone}</p>}
                                    {apt.service_address && <p><span className="text-muted-foreground">📍 </span>{apt.service_address}</p>}
                                    {apt.parking_instructions && <p className="text-muted-foreground/80 italic">Parking: {apt.parking_instructions}</p>}
                                    {apt.notes && <p className="italic"><span className="text-muted-foreground">Notes: </span>"{apt.notes}"</p>}
                                    <div><span className="text-muted-foreground">Status: </span><Badge variant="outline" className={`text-[10px] ${statusColor(apt.status)} border-0`}>{(apt.status || 'scheduled').replace(/_/g, ' ')}</Badge></div>
                                  </div>
                                  <div className="flex gap-1.5 pt-2 border-t border-border/30">
                                    <Button size="sm" variant="outline" className="h-7 text-[10px] border-primary/40 text-primary hover:bg-primary/10 flex-1" onClick={(e) => { e.stopPropagation(); onEditAppointment?.(apt); }} data-testid={`quick-edit-${apt.id}`}>
                                      <Edit className="w-3 h-3 mr-1" />Edit
                                    </Button>
                                    {p && onViewPatient && (
                                      <Button size="sm" variant="outline" className="h-7 text-[10px] border-primary/40 text-primary hover:bg-primary/10 flex-1" onClick={(e) => { e.stopPropagation(); onViewPatient(p); }} data-testid={`quick-chart-${apt.id}`}>
                                        <FileText className="w-3 h-3 mr-1" />Chart
                                      </Button>
                                    )}
                                    {apt.status === 'scheduled' && !apt.checked_in_at && onCheckIn && (
                                      <Button size="sm" className="h-7 text-[10px] bg-emerald-500/80 hover:bg-emerald-500 text-white flex-1" onClick={(e) => { e.stopPropagation(); onCheckIn(apt); }} data-testid={`quick-checkin-${apt.id}`}>
                                        <CheckCircle2 className="w-3 h-3 mr-1" />Check In
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </HoverCardContent>
                            </HoverCard>
                          );
                        })}
                        {isEmpty && (
                          <div className="text-[11px] italic text-muted-foreground/40 py-1.5 pl-1 group-hover:text-primary/70 transition-colors">
                            + Book at {label}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* RIGHT: Booking Panel */}
      <Card className="lg:col-span-4 gold-border bg-card/80" data-testid="booking-panel">
        <CardContent className="p-4 space-y-3">
          {!selectedDate ? (
            <div className="text-center py-8 space-y-2">
              <CalendarIcon className="w-10 h-10 mx-auto text-primary/30" />
              <p className="text-sm font-semibold text-primary">Select a date</p>
              <p className="text-xs text-muted-foreground">Click any day on the calendar to book a new appointment or view that day's schedule.</p>
            </div>
          ) : (
            <>
              <div className="pb-3 border-b border-border/40">
                <p className="text-[10px] uppercase tracking-widest text-primary/70">Booking for</p>
                <p className="text-base font-semibold gold-gradient-text">{format(selectedDate, 'EEEE, MMM d, yyyy')}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{(apptsByDay[format(selectedDate, 'yyyy-MM-dd')] || []).length} existing appointment(s) · {selectedDateBlocks.length} blocked slot(s)</p>
              </div>

              {/* Blocked Times */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1.5"><span className="text-rose-400">🚫</span>Blocked Times</Label>
                  <Button type="button" variant="ghost" size="sm" className="h-6 text-[10px] text-rose-400 hover:bg-rose-500/10" onClick={() => setShowBlockForm(v => !v)} data-testid="toggle-block-form">
                    {showBlockForm ? 'Cancel' : '+ Block time'}
                  </Button>
                </div>
                {selectedDateBlocks.length === 0 && !showBlockForm && (
                  <p className="text-[11px] text-muted-foreground italic">No blocked times. Patients can be booked anytime between 8 AM – 12 AM.</p>
                )}
                {selectedDateBlocks.map(b => (
                  <div key={b.id} className="flex items-center justify-between p-2 rounded-md border border-rose-500/30 bg-rose-500/5" data-testid={`blocked-row-${b.id}`}>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-rose-300">{fmtTime12(b.start_time)} – {fmtTime12(b.end_time)}</p>
                      {b.reason && <p className="text-[10px] text-muted-foreground italic truncate">{b.reason}</p>}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-[10px] text-rose-300/80 hover:bg-rose-500/10 hover:text-rose-200"
                        onClick={() => handleUnblockRecurrence(b)}
                        title={`Unblock every future ${selectedDate ? format(selectedDate, 'EEEE') : 'weekday'} at this time`}
                        data-testid={`unblock-series-${b.id}`}
                      >
                        Unblock series
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-rose-400 hover:bg-rose-500/10" onClick={() => handleUnblock(b.id)} title="Unblock only this day" data-testid={`unblock-${b.id}`}><X className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
                {showBlockForm && (
                  <div className="p-2.5 rounded-md border border-rose-500/30 bg-rose-500/5 space-y-2" data-testid="block-form">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Start time</Label>
                        <Select value={blockStart} onValueChange={setBlockStart}>
                          <SelectTrigger className="h-8 text-xs" data-testid="block-start"><SelectValue /></SelectTrigger>
                          <SelectContent className="max-h-[200px]">
                            {TIME_OPTIONS.filter(t => t !== '00:00').map(t => <SelectItem key={t} value={t}>{fmtTime12(t)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Duration</Label>
                        <Select value={String(blockDuration)} onValueChange={(v) => setBlockDuration(Number(v))}>
                          <SelectTrigger className="h-8 text-xs" data-testid="block-duration"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="15">15 min</SelectItem>
                            <SelectItem value="30">30 min</SelectItem>
                            <SelectItem value="60">1 hour</SelectItem>
                            <SelectItem value="90">1.5 hours</SelectItem>
                            <SelectItem value="120">2 hours</SelectItem>
                            <SelectItem value="180">3 hours</SelectItem>
                            <SelectItem value="240">4 hours</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Input placeholder="Reason (e.g. Extended treatment)" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} className="h-8 text-xs" data-testid="block-reason" />

                    {/* Recurring block */}
                    <div className="pt-1.5 border-t border-rose-500/20 space-y-2">
                      <label className="flex items-center gap-2 text-[11px] text-rose-200 cursor-pointer select-none" data-testid="block-repeat-toggle">
                        <input
                          type="checkbox"
                          checked={blockRepeat}
                          onChange={(e) => {
                            const on = e.target.checked;
                            setBlockRepeat(on);
                            // pre-select the selected date's weekday when enabling
                            if (on && blockDays.length === 0 && selectedDate) {
                              setBlockDays([selectedDate.getDay()]);
                            }
                          }}
                          className="accent-rose-500 h-3.5 w-3.5"
                        />
                        <span>Repeat this block on…</span>
                      </label>
                      {blockRepeat && (
                        <div className="space-y-2 pl-1">
                          <div>
                            <p className="text-[10px] text-muted-foreground mb-1">Days of week</p>
                            <div className="flex flex-wrap gap-1">
                              {['S','M','T','W','T','F','S'].map((lbl, i) => {
                                const active = blockDays.includes(i);
                                return (
                                  <button
                                    key={i}
                                    type="button"
                                    onClick={() => setBlockDays(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i])}
                                    className={`h-7 w-7 rounded-md text-[11px] font-semibold border transition-colors ${active ? 'bg-rose-500 text-white border-rose-500' : 'bg-transparent text-rose-300 border-rose-500/30 hover:bg-rose-500/10'}`}
                                    data-testid={`block-dow-${i}`}
                                    aria-pressed={active}
                                  >
                                    {lbl}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-[10px] text-muted-foreground">For the next</p>
                            <Select value={String(blockWeeks)} onValueChange={(v) => setBlockWeeks(Number(v))}>
                              <SelectTrigger className="h-7 w-20 text-xs" data-testid="block-weeks"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {[1,2,3,4,5,6,7,8,10,12].map(w => <SelectItem key={w} value={String(w)}>{w} week{w === 1 ? '' : 's'}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <p className="text-[10px] text-rose-200/70 italic">
                            Starts {selectedDate ? format(selectedDate, 'MMM d, yyyy') : '—'}. Existing identical blocks are skipped.
                          </p>
                        </div>
                      )}
                    </div>

                    <Button onClick={handleBlockSlot} disabled={blockSaving} className="w-full h-8 text-xs bg-rose-500/80 hover:bg-rose-500 text-white" data-testid="block-save">
                      {blockSaving
                        ? 'Blocking…'
                        : blockRepeat
                          ? `Block ${fmtTime12(blockStart)}–${fmtTime12(addEndTime(blockStart, blockDuration))} · ${Math.max(blockDays.length, 1)} day${Math.max(blockDays.length,1)===1?'':'s'} × ${blockWeeks} wk${blockWeeks===1?'':'s'}`
                          : `Block ${fmtTime12(blockStart)} – ${fmtTime12(addEndTime(blockStart, blockDuration))}`}
                    </Button>
                  </div>
                )}
              </div>

              {/* Patient */}
              <div className="space-y-1">
                <Label className="text-xs">Patient *</Label>
                <Popover open={patientPopoverOpen} onOpenChange={setPatientPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between text-sm" data-testid="bp-patient-trigger">
                      {patientId ? (
                        <span className="flex items-center gap-2"><Avatar className="h-5 w-5"><AvatarFallback className="bg-primary/10 text-primary text-[10px]">{getInitials(patientById[patientId]?.first_name, patientById[patientId]?.last_name)}</AvatarFallback></Avatar>{patientById[patientId]?.first_name} {patientById[patientId]?.last_name}</span>
                      ) : (
                        <span className="text-muted-foreground">Search existing or follow-up patient…</span>
                      )}
                      <Search className="w-3.5 h-3.5 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search by name or phone…" value={patientSearch} onValueChange={setPatientSearch} />
                      <CommandList>
                        <CommandEmpty>No patient found.</CommandEmpty>
                        <CommandGroup>
                          {filteredPatients.map(p => (
                            <CommandItem
                              key={p.id}
                              value={`${p.first_name} ${p.last_name} ${p.phone || ''}`}
                              onSelect={() => { setPatientId(p.id); setPatientPopoverOpen(false); }}
                              data-testid={`bp-patient-option-${p.id}`}
                            >
                              <Avatar className="h-6 w-6 mr-2"><AvatarFallback className="bg-primary/10 text-primary text-[10px]">{getInitials(p.first_name, p.last_name)}</AvatarFallback></Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm">{p.first_name} {p.last_name}</p>
                                {p.phone && <p className="text-[10px] text-muted-foreground">{p.phone}</p>}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <Button type="button" variant="ghost" size="sm" className="text-primary hover:bg-primary/10 w-full" onClick={() => setNewPatientOpen(true)} data-testid="bp-new-patient-btn">
                  <UserPlus className="w-3.5 h-3.5 mr-1" />+ New Patient (quick add)
                </Button>
              </div>

              {/* Service */}
              <div className="space-y-1">
                <Label className="text-xs">Service *</Label>
                <Select value={serviceId} onValueChange={setServiceId}>
                  <SelectTrigger className="text-sm" data-testid="bp-service-select"><SelectValue placeholder="Select IV drip or IM injection" /></SelectTrigger>
                  <SelectContent>
                    {grouped.hydration.length > 0 && <div className="px-2 py-1 text-[10px] font-semibold text-primary/70 uppercase tracking-wider">Hydration</div>}
                    {grouped.hydration.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · ${s.price?.toFixed(0)}</SelectItem>)}
                    {grouped.iv_drip.length > 0 && <div className="px-2 py-1 text-[10px] font-semibold text-primary/70 uppercase tracking-wider mt-1">IV Drips</div>}
                    {grouped.iv_drip.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · ${s.price?.toFixed(0)}</SelectItem>)}
                    {grouped.injection.length > 0 && <div className="px-2 py-1 text-[10px] font-semibold text-primary/70 uppercase tracking-wider mt-1">IM Injections</div>}
                    {grouped.injection.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · ${s.price?.toFixed(0)}</SelectItem>)}
                    {grouped.other.length > 0 && <div className="px-2 py-1 text-[10px] font-semibold text-primary/70 uppercase tracking-wider mt-1">Other</div>}
                    {grouped.other.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Time */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Time</Label>
                  <button type="button" onClick={() => setShowAllTimes(v => !v)} className="text-[10px] text-primary/80 hover:text-primary underline underline-offset-2" data-testid="toggle-all-times">
                    {showAllTimes ? 'Show 2-hour slots' : '+ Custom time (15-min)'}
                  </button>
                </div>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger className="text-sm" data-testid="bp-time-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[280px]">
                    {(showAllTimes ? TIME_OPTIONS : TIME_OPTIONS_PRESET).map(t => <SelectItem key={t} value={t}>{fmtTime12(t)}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!showAllTimes && (
                  <p className="text-[10px] text-muted-foreground italic">Every 2 hours · Tap "+ Custom time" for in-between slots.</p>
                )}
              </div>

              {/* Staff */}
              <div className="space-y-1">
                <Label className="text-xs">Assign nurse</Label>
                <div className="flex flex-wrap gap-1">
                  {staff.filter(s => s.is_active !== false).map(s => {
                    const on = staffIds.includes(s.id);
                    const displayName = `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email || 'Staff';
                    return (
                      <button key={s.id} type="button"
                        onClick={() => setStaffIds(prev => on ? prev.filter(x => x !== s.id) : [...prev, s.id])}
                        className={`text-[11px] px-2.5 py-1 rounded-full border transition-all ${on ? 'bg-primary text-primary-foreground border-primary shadow-[0_0_0_2px_rgba(212,175,55,0.15)]' : 'bg-muted/30 text-muted-foreground border-border/40 hover:border-primary/50 hover:text-primary'}`}
                        data-testid={`bp-staff-${s.id}`}
                        title={s.role ? `${displayName} · ${s.role}` : displayName}>
                        {displayName}{on ? ' ✓' : ''}
                      </button>
                    );
                  })}
                  {staff.filter(s => s.is_active !== false).length === 0 && (
                    <p className="text-[10px] italic text-muted-foreground" data-testid="bp-no-staff-hint">
                      No nurses yet — add one under the <strong className="not-italic text-primary">Staff</strong> tab.
                    </p>
                  )}
                </div>
              </div>

              {/* Service Address (Mobile) */}
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1"><span className="text-primary">📍</span> Service Address <span className="text-muted-foreground/60 font-normal">(mobile visit location)</span></Label>
                <Input
                  placeholder="123 Main St, Apt 4B, City, State ZIP"
                  value={serviceAddress}
                  onChange={(e) => setServiceAddress(e.target.value)}
                  className="text-sm"
                  data-testid="bp-service-address"
                />
              </div>

              {/* Parking instructions */}
              <div className="space-y-1">
                <Label className="text-xs">Parking / Arrival Instructions <span className="text-muted-foreground/60 font-normal">(optional)</span></Label>
                <Input
                  placeholder="Gate code 4521, park in driveway, ring buzzer 2…"
                  value={parkingInstructions}
                  onChange={(e) => setParkingInstructions(e.target.value)}
                  className="text-sm"
                  data-testid="bp-parking"
                />
              </div>

              {/* Notes */}
              <div className="space-y-1">
                <Label className="text-xs">Notes (optional)</Label>
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Special accommodations, follow-up reason…" className="text-sm" data-testid="bp-notes" />
              </div>

              {/* Repeat */}
              <div className="space-y-1.5 rounded-lg border border-border/50 p-2.5" data-testid="bp-repeat-section">
                <Label className="text-xs flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5 text-primary" />Repeat this visit</Label>
                <div className="flex items-center gap-2">
                  <Select value={repeatFreq} onValueChange={setRepeatFreq}>
                    <SelectTrigger className="h-8 text-sm flex-1" data-testid="bp-repeat-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Does not repeat</SelectItem>
                      <SelectItem value="weekly">Every week</SelectItem>
                      <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                      <SelectItem value="monthly">Every month</SelectItem>
                    </SelectContent>
                  </Select>
                  {repeatFreq !== 'none' && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      for <Input type="number" min={2} max={26} value={repeatCount} onChange={(e) => setRepeatCount(Math.min(26, Math.max(2, Number(e.target.value) || 2)))} className="h-8 w-[64px] text-sm" data-testid="bp-repeat-occurrences" /> visits
                    </div>
                  )}
                </div>
                {repeatFreq !== 'none' && selectedDate && (
                  <p className="text-[11px] text-muted-foreground" data-testid="bp-repeat-preview">
                    {(() => { const step = repeatFreq === 'weekly' ? 7 : repeatFreq === 'biweekly' ? 14 : 0; const last = new Date(selectedDate); if (step) last.setDate(selectedDate.getDate() + step * (repeatCount - 1)); else last.setMonth(selectedDate.getMonth() + (repeatCount - 1)); return `${repeatCount} visits · ${format(selectedDate, 'MMM d')} → ${format(last, 'MMM d, yyyy')}`; })()} · confirmation sent for the first visit
                  </p>
                )}
                {repeatFreq !== 'none' && seriesPreview && seriesPreview.conflict_count > 0 && (
                  <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-2 space-y-1.5" data-testid="bp-series-conflicts">
                    <p className="text-xs font-medium text-amber-300 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />{seriesPreview.conflict_count} of {repeatCount} visits conflict</p>
                    <ul className="space-y-0.5">
                      {seriesPreview.occurrences.filter(o => o.conflicts.length).map(o => (
                        <li key={o.index} className="text-[11px] text-amber-200/90" data-testid={`bp-series-conflict-${o.index}`}>
                          <b>#{o.index} · {format(new Date(o.start_datetime), 'EEE MMM d')}</b> — {o.conflicts.map(c => c.message).join('; ')}
                        </li>
                      ))}
                    </ul>
                    <label className="flex items-center gap-2 text-[11px] text-foreground cursor-pointer pt-0.5">
                      <input type="checkbox" checked={skipConflicts} onChange={(e) => setSkipConflicts(e.target.checked)} className="accent-amber-400" data-testid="bp-skip-conflicts" />
                      Skip the conflicting dates (book {repeatCount - seriesPreview.conflict_count} visits)
                    </label>
                  </div>
                )}
                {repeatFreq !== 'none' && seriesPreview && seriesPreview.conflict_count === 0 && serviceId && (
                  <p className="text-[11px] text-emerald-300 flex items-center gap-1" data-testid="bp-series-clear"><CheckCircle2 className="w-3 h-3" />No conflicts — all {repeatCount} dates are free</p>
                )}
              </div>

              <Button onClick={handleSave} disabled={saving} className="w-full bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 hover:from-amber-400 hover:via-yellow-400 hover:to-amber-500 text-black font-semibold shadow-md shadow-amber-500/20" data-testid="bp-save-btn">
                {saving ? 'Saving…' : 'Save Appointment'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Quick new patient modal */}
      <Dialog open={newPatientOpen} onOpenChange={setNewPatientOpen}>
        <DialogContent className="max-w-md" data-testid="bp-new-patient-modal">
          <DialogHeader>
            <DialogTitle>Quick Patient Intake</DialogTitle>
            <DialogDescription>Add minimum info now. Full intake can be completed from the Patients tab.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label className="text-xs">First Name *</Label><Input value={newPatient.first_name} onChange={(e) => setNewPatient({ ...newPatient, first_name: e.target.value })} data-testid="bp-np-firstname" /></div>
              <div className="space-y-1"><Label className="text-xs">Last Name *</Label><Input value={newPatient.last_name} onChange={(e) => setNewPatient({ ...newPatient, last_name: e.target.value })} data-testid="bp-np-lastname" /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={newPatient.phone} onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })} placeholder="555-123-4567" /></div>
            <div className="space-y-1"><Label className="text-xs">Email</Label><Input type="email" value={newPatient.email} onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewPatientOpen(false)}>Cancel</Button>
            <Button onClick={handleCreatePatient} disabled={creatingPatient} className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold" data-testid="bp-np-save">
              {creatingPatient ? 'Creating…' : 'Create & Select'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== APPOINTMENT MODAL ====================
